// Sidebar (RouterMenu.vue) <-> BrandOverview2 (index.vue) are separate
// component trees (Layout vs router-view) with no direct reference to each
// other, so the "Brand Overview" sidebar section talks to the page through
// this shared, non-persisted state instead:
//   - groupBrands / activeBrandKey: the PAGE reports these to the sidebar
//     (its own groupData.brands, and which brand key it's currently drilled
//     into) so the sidebar can render the same brand list and highlight the
//     right row without fetching or tracking any of that itself.
//   - pendingBrandKey / hasPendingBrandNav: the SIDEBAR reports a brand-nav
//     intent (a specific key, or null for "back to portfolio") the page
//     applies to its own selectedBrandKey — on mount if the page wasn't
//     there yet, or immediately via watcher if it already was — so switching
//     brands from the sidebar never needs a full page reload.
export default {
  namespaced: true,
  state: {
    groupBrands: null,
    activeBrandKey: null,
    // Mirrors index.vue's brandViewMode ('overview'/'details') for whichever
    // brand is active — lets the sidebar's per-brand "Details" row highlight
    // only while that brand is showing its Details tab specifically, same
    // idea as activeBrandKey/activeDrillKey.
    activeBrandViewMode: null,
    // Mirrors index.vue's portfolioOwnerDrill.dutyKey (or null) so the
    // "Active Signal Details" sidebar row can highlight itself only while
    // that specific drill is actually open — same idea as activeBrandKey,
    // just for a drill instead of a brand.
    activeDrillKey: null,
    hasPendingBrandNav: false,
    pendingBrandKey: null,
    // Set via the separate SET_PENDING_BRAND_VIEW_MODE mutation, right after
    // SET_PENDING_BRAND_NAV, only by the sidebar's per-brand "Details" row —
    // index.vue applies it right after landing on the brand, instead of that
    // brand's default Overview tab.
    pendingBrandViewMode: null,
    // Bumped on every SET_PENDING_BRAND_NAV. The page watches THIS, not
    // hasPendingBrandNav — a second sidebar click while an earlier intent is
    // somehow still uncleared would leave that boolean true→true and never
    // re-fire the watcher.
    navSeq: 0,
    // Same "sidebar reports an intent, page applies it" shape as pendingBrandKey
    // above, for the "Active Signal Details" sidebar shortcut (RouterMenu.vue)
    // straight into a portfolio-owner drill (e.g. dutyKey "hotel_operations")
    // instead of a brand. Kept as its own key/seq pair rather than overloading
    // pendingBrandKey — a drill intent and a brand intent are different targets
    // and can't share one "which one is this" slot.
    hasPendingDrillNav: false,
    pendingDrillKey: null,
    drillSeq: 0,
  },
  mutations: {
    SET_GROUP_BRANDS(state, brands) {
      state.groupBrands = brands || null;
    },
    SET_ACTIVE_BRAND_KEY(state, key) {
      state.activeBrandKey = key || null;
    },
    SET_ACTIVE_BRAND_VIEW_MODE(state, mode) {
      state.activeBrandViewMode = mode || null;
    },
    SET_ACTIVE_DRILL_KEY(state, key) {
      state.activeDrillKey = key || null;
    },
    // `key` is the target brand's key, or `null` to mean "back to portfolio
    // overview" — a real key and "go to portfolio" both need to reach the
    // page, so this can't just use `pendingBrandKey`'s own truthiness as the
    // "is there a pending intent" flag.
    SET_PENDING_BRAND_NAV(state, key) {
      state.hasPendingBrandNav = true;
      state.pendingBrandKey = key || null;
      state.navSeq += 1;
    },
    CLEAR_PENDING_BRAND_NAV(state) {
      state.hasPendingBrandNav = false;
      state.pendingBrandKey = null;
      state.pendingBrandViewMode = null;
    },
    // Committed alongside SET_PENDING_BRAND_NAV (a separate mutation, since
    // Vuex commit only forwards one payload) only by the sidebar's per-brand
    // "Details" row — a plain brand click never sets this, leaving it null
    // (= that brand's default Overview tab).
    SET_PENDING_BRAND_VIEW_MODE(state, mode) {
      state.pendingBrandViewMode = mode || null;
    },
    SET_PENDING_DRILL_NAV(state, dutyKey) {
      state.hasPendingDrillNav = true;
      state.pendingDrillKey = dutyKey || null;
      state.drillSeq += 1;
    },
    CLEAR_PENDING_DRILL_NAV(state) {
      state.hasPendingDrillNav = false;
      state.pendingDrillKey = null;
    },
  },
};
